// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormDatePicker } from '@/components/form-date-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import {
  DeleteRounded,
  HomeRepairServiceOutlined,
  LinkRounded,
  SellOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  flourishFieldBaseSx,
  flourishFieldSx,
} from '@/hooks/use-linked-field-flourish';
import { ItemSearch } from '@/items/components/item-search';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { CustomPriorityGroup } from '@/location/components/location-search-options';
import { TRANSACTION_TYPE } from '@/project/api/types';
import { PROJECT_LINE_ITEM_DATE_SHORTCUTS } from '@/project/constants/project-line-item-date-shortcuts';
import { ProjectLineItemsFormData } from '@/project/types/project-line-items-form-schema';
import { DateOrderErrors } from '@/project/utilities/validate-line-item-dates';
import { SparseLocation } from '@/sparse/api/types';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

interface Props {
  control: Control<ProjectLineItemsFormData>;
  register: UseFormRegister<ProjectLineItemsFormData>;
  setValue: UseFormSetValue<ProjectLineItemsFormData>;
  clearErrors: UseFormClearErrors<ProjectLineItemsFormData>;
  lineItemErrors:
    | FieldErrors<ProjectLineItemsFormData['lineItems'][number]>
    | undefined;
  index: number;
  field: FieldArrayWithId<ProjectLineItemsFormData, 'lineItems'>;
  watchedLineItem: ProjectLineItemsFormData['lineItems'][number] | undefined;
  dateOrderErrors: DateOrderErrors;
  projectStart: DateTime | null;
  projectEnd: DateTime | null;
  customerId: string | undefined;
  posInGroup: number;
  isMultiItemGroup: boolean;
  previousPhaseEndDate: DateTime | null;
  flourish: { isActive: (path: string) => boolean };
  durationDisplay: React.ReactNode;
  onDelete: () => void;
  onTransactionTypeChange: (index: number, value: string) => void;
  onStartDateChange: (index: number, date: DateTime | null) => void;
  onEndDateChange: (index: number, date: DateTime | null) => void;
  sortLocations: (a: SparseLocation, b: SparseLocation) => number;
  locationPriorityGroup: CustomPriorityGroup | undefined;
  onCreateLocation: (name: string) => void;
}

interface ItemSearchFieldProps {
  control: Control<ProjectLineItemsFormData>;
  index: number;
  setValue: UseFormSetValue<ProjectLineItemsFormData>;
  clearErrors: UseFormClearErrors<ProjectLineItemsFormData>;
  lineItemErrors:
    | FieldErrors<ProjectLineItemsFormData['lineItems'][number]>
    | undefined;
}

function ItemSearchField(itemProps: ItemSearchFieldProps) {
  const item = useWatch({
    control: itemProps.control,
    name: `lineItems.${itemProps.index}.item`,
  });
  const itemError = itemProps.lineItemErrors?.item;
  const prefix = `lineItems.${itemProps.index}` as const;

  return (
    <ItemSearch
      label="SKU"
      value={item}
      error={!!itemError}
      helperText={itemError?.message}
      popperWidth={450}
      onChange={(value) => {
        itemProps.setValue(`${prefix}.item`, value, { shouldDirty: true });
        if (value !== null && !value.isPhysical) {
          itemProps.setValue(
            `${prefix}.transactionType`,
            TRANSACTION_TYPE.SERVICE
          );
        }
        if (itemError && value !== null) {
          itemProps.clearErrors(`${prefix}.item`);
        }
      }}
    />
  );
}

export function ProjectLineItemFormRow(props: Props) {
  const theme = useTheme();
  const watchedLineItem = props.watchedLineItem;
  const hasPhysicalItem =
    watchedLineItem?.item !== null &&
    (watchedLineItem?.item?.isPhysical ?? false);

  const startDateValue = watchedLineItem?.startOn ?? null;
  const endDateValue = watchedLineItem?.endOn ?? null;
  const deleteDisabled = watchedLineItem?.hasInvoicedCharges ?? false;
  // End date reference: default to the project end date. If the start date
  // is after the project end, default to the day after the project end.
  let endReferenceDate: DateTime | undefined;
  if (!props.projectEnd) {
    endReferenceDate = startDateValue?.plus({ days: 1 }) ?? undefined;
  } else if (startDateValue && startDateValue > props.projectEnd) {
    endReferenceDate = props.projectEnd.plus({ days: 1 });
  } else {
    endReferenceDate = props.projectEnd;
  }

  // Start date reference: previous phase end (for continuous segments) →
  // project start. No fallback to today — if neither exists, leave undefined.
  const startReferenceDate =
    props.previousPhaseEndDate ?? props.projectStart ?? undefined;
  const isStartBeforeProject =
    startDateValue?.isValid &&
    props.projectStart &&
    startDateValue < props.projectStart;
  const isStartAfterProject =
    startDateValue?.isValid &&
    props.projectEnd &&
    startDateValue > props.projectEnd;
  const isEndAfterProject =
    endDateValue?.isValid &&
    props.projectEnd &&
    endDateValue > props.projectEnd;
  const isEndBeforeProject =
    endDateValue?.isValid &&
    props.projectStart &&
    endDateValue < props.projectStart;

  const startFlourishActive = props.flourish.isActive(
    `lineItems.${props.index}.startOn`
  );
  const endFlourishActive = props.flourish.isActive(
    `lineItems.${props.index}.endOn`
  );

  const dateFieldBaseSx = flourishFieldBaseSx();
  // Active-flourish styling for line-item date fields layers a link-icon swap
  // (calendar adornment hidden while flourishing; restored on hover) on top
  // of the shared flourish highlight.
  const flourishSx = {
    ...flourishFieldSx(theme, true),
    '& .MuiInputAdornment-root': { display: 'none' },
    '& .linked-field-icon': { display: 'flex' },
    '&:hover .MuiInputAdornment-root': { display: 'flex' },
    '&:hover .linked-field-icon': { display: 'none' },
  };

  return (
    <Box key={props.field.id}>
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr auto"
        columnGap={1}
        rowGap={1}
        alignItems="start"
      >
        {/* Row 1, Col 1: Item + Qty */}
        <Box display="flex" gap={1} alignItems="start">
          <Box flex={1}>
            <ItemSearchField
              control={props.control}
              index={props.index}
              setValue={props.setValue}
              clearErrors={props.clearErrors}
              lineItemErrors={props.lineItemErrors}
            />
          </Box>
          <Box sx={{ width: 120, flexShrink: 0 }}>
            <FormNumberTextField
              control={props.control}
              label="Qty"
              name={`lineItems.${props.index}.quantity`}
              numeric
              onChange={(values) => {
                if (props.lineItemErrors?.quantity && values.value !== '') {
                  props.clearErrors(`lineItems.${props.index}.quantity`);
                }
              }}
            />
          </Box>
        </Box>
        {/* Row 1, Col 2: Location */}
        <FormLocationSearch
          control={props.control}
          label="Location (optional)"
          name={`lineItems.${props.index}.location`}
          grouping={['customer']}
          sort={props.sortLocations}
          priorityCustomerId={props.customerId}
          customPriorityGroup={props.locationPriorityGroup}
          onCreate={props.onCreateLocation}
          popperWidth={450}
        />
        {/* Row 1, Col 3: Transaction toggle */}
        <Controller
          control={props.control}
          name={`lineItems.${props.index}.transactionType`}
          render={({ field: transactionField }) => {
            const isChildPhase = props.posInGroup > 0 && props.isMultiItemGroup;
            const effectiveValue = hasPhysicalItem
              ? transactionField.value
              : TRANSACTION_TYPE.SERVICE;

            return (
              <TransactionToggle
                hasPhysicalItem={hasPhysicalItem}
                disabled={isChildPhase}
                value={effectiveValue}
                onChange={(value) => {
                  transactionField.onChange(value);
                  props.onTransactionTypeChange(props.index, value);
                }}
              />
            );
          }}
        />

        {/* Row 2, Col 1: Start + End dates (50/50) */}
        <Box display="flex" gap={1} alignItems="start">
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                position: 'relative',
                ...(props.dateOrderErrors[props.index]?.start
                  ? {
                      '& .MuiOutlinedInput-root': {
                        borderColor: 'error.main',
                      },
                    }
                  : startFlourishActive
                    ? flourishSx
                    : isStartBeforeProject || isStartAfterProject
                      ? {
                          ...dateFieldBaseSx,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgb(255, 244, 229)',
                          },
                        }
                      : dateFieldBaseSx),
              }}
            >
              <FormDatePicker
                control={props.control}
                name={`lineItems.${props.index}.startOn`}
                label="Start"
                isClearable
                shortcuts={PROJECT_LINE_ITEM_DATE_SHORTCUTS}
                referenceDate={startReferenceDate}
                onChange={(date) => {
                  if (date === null) {
                    props.setValue(`lineItems.${props.index}.startOn`, null, {
                      shouldDirty: true,
                    });
                    props.onStartDateChange(props.index, null);
                    // Start cleared: "start required when end set" stays if end exists
                    // But "end required when start set" on endOn can clear
                    if (
                      props.lineItemErrors?.endOn &&
                      !watchedLineItem?.endOn
                    ) {
                      props.clearErrors(`lineItems.${props.index}.endOn`);
                    }
                    return;
                  }
                  if (date.isValid) {
                    props.setValue(`lineItems.${props.index}.startOn`, date, {
                      shouldDirty: true,
                    });
                    props.onStartDateChange(props.index, date);
                    // Start set: "start required" error on startOn can clear
                    if (props.lineItemErrors?.startOn) {
                      props.clearErrors(`lineItems.${props.index}.startOn`);
                    }
                    // End date error may also resolve (e.g., end > start now valid)
                    if (props.lineItemErrors?.endOn && watchedLineItem?.endOn) {
                      if (
                        watchedLineItem.endOn.isValid &&
                        watchedLineItem.endOn > date
                      ) {
                        props.clearErrors(`lineItems.${props.index}.endOn`);
                      }
                    }
                  }
                }}
              />
              <Box
                className="linked-field-icon"
                sx={{
                  position: 'absolute',
                  top: 0,
                  height: 40,
                  right: 8,
                  display: 'none',
                  alignItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <LinkRounded fontSize="small" color="primary" />
              </Box>
            </Box>
            {props.dateOrderErrors[props.index]?.start && (
              <Typography
                variant="caption"
                color="error"
                display="block"
                mt={0.5}
                pl={2}
              >
                {props.dateOrderErrors[props.index]?.start}
              </Typography>
            )}
            {!props.dateOrderErrors[props.index]?.start &&
              isStartAfterProject &&
              props.projectEnd && (
                <Typography
                  variant="caption"
                  color="warning.dark"
                  display="block"
                  mt={0.5}
                  pl={2}
                >
                  Starts{' '}
                  {pluralize(
                    Math.abs(
                      Math.round(
                        startDateValue!.diff(props.projectEnd, 'days').days
                      )
                    ),
                    { singular: 'day' }
                  )}{' '}
                  after the project ends
                </Typography>
              )}
            {!props.dateOrderErrors[props.index]?.start &&
              !isStartAfterProject &&
              isStartBeforeProject &&
              props.projectStart && (
                <Typography
                  variant="caption"
                  color="warning.dark"
                  display="block"
                  mt={0.5}
                  pl={2}
                >
                  Starts{' '}
                  {pluralize(
                    Math.abs(
                      Math.round(
                        props.projectStart.diff(startDateValue!, 'days').days
                      )
                    ),
                    { singular: 'day' }
                  )}{' '}
                  before the project starts
                </Typography>
              )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                position: 'relative',
                ...(props.dateOrderErrors[props.index]?.end
                  ? {
                      '& .MuiOutlinedInput-root': {
                        borderColor: 'error.main',
                      },
                    }
                  : endFlourishActive
                    ? flourishSx
                    : isEndAfterProject || isEndBeforeProject
                      ? {
                          ...dateFieldBaseSx,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgb(255, 244, 229)',
                          },
                        }
                      : dateFieldBaseSx),
              }}
            >
              <FormDatePicker
                control={props.control}
                name={`lineItems.${props.index}.endOn`}
                label="End"
                isClearable
                shortcuts={PROJECT_LINE_ITEM_DATE_SHORTCUTS}
                referenceDate={endReferenceDate}
                onChange={(date) => {
                  if (date === null) {
                    props.setValue(`lineItems.${props.index}.endOn`, null, {
                      shouldDirty: true,
                    });
                    props.onEndDateChange(props.index, null);
                    // End cleared: "end required when start set" stays if start exists
                    // But "start required when end set" on startOn can clear
                    if (
                      props.lineItemErrors?.startOn &&
                      !watchedLineItem?.startOn
                    ) {
                      props.clearErrors(`lineItems.${props.index}.startOn`);
                    }
                    return;
                  }
                  if (date.isValid) {
                    props.setValue(`lineItems.${props.index}.endOn`, date, {
                      shouldDirty: true,
                    });
                    props.onEndDateChange(props.index, date);
                    // End set: "end required" error on endOn can clear
                    if (props.lineItemErrors?.endOn) {
                      props.clearErrors(`lineItems.${props.index}.endOn`);
                    }
                    // Start error may also resolve (e.g., pairing now satisfied)
                    if (
                      props.lineItemErrors?.startOn &&
                      watchedLineItem?.startOn
                    ) {
                      props.clearErrors(`lineItems.${props.index}.startOn`);
                    }
                  }
                }}
              />
              <Box
                className="linked-field-icon"
                sx={{
                  position: 'absolute',
                  top: 0,
                  height: 40,
                  right: 8,
                  display: 'none',
                  alignItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <LinkRounded fontSize="small" color="primary" />
              </Box>
            </Box>
            {props.dateOrderErrors[props.index]?.end && (
              <Typography
                variant="caption"
                color="error"
                display="block"
                mt={0.5}
                pl={2}
              >
                {props.dateOrderErrors[props.index]?.end}
              </Typography>
            )}
            {!props.dateOrderErrors[props.index]?.end &&
              isEndBeforeProject &&
              props.projectStart && (
                <Typography
                  variant="caption"
                  color="warning.dark"
                  display="block"
                  mt={0.5}
                  pl={2}
                >
                  Ends{' '}
                  {pluralize(
                    Math.abs(
                      Math.round(
                        props.projectStart.diff(endDateValue!, 'days').days
                      )
                    ),
                    { singular: 'day' }
                  )}{' '}
                  before the project starts
                </Typography>
              )}
            {!props.dateOrderErrors[props.index]?.end &&
              !isEndBeforeProject &&
              isEndAfterProject &&
              props.projectEnd && (
                <Typography
                  variant="caption"
                  color="warning.dark"
                  display="block"
                  mt={0.5}
                  pl={2}
                >
                  Ends{' '}
                  {pluralize(
                    Math.abs(
                      Math.round(
                        endDateValue!.diff(props.projectEnd, 'days').days
                      )
                    ),
                    { singular: 'day' }
                  )}{' '}
                  after the project ends
                </Typography>
              )}
          </Box>
        </Box>
        {/* Row 2, Cols 2-3: Details + Delete */}
        <Box
          display="flex"
          gap={1}
          alignItems="start"
          sx={{ gridColumn: '2 / 4' }}
        >
          <Box flex={1}>
            <TextField
              size="small"
              label="Details"
              fullWidth
              inputProps={props.register(`lineItems.${props.index}.details`)}
            />
          </Box>
          {deleteDisabled ? (
            <Tooltip
              title="Line items with invoiced charges cannot be removed"
              arrow
            >
              <span>
                <IconButton disabled>
                  <DeleteRounded fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <IconButton onClick={props.onDelete}>
              <DeleteRounded fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Row 3: Duration display */}
        {props.durationDisplay && (
          <Box sx={{ gridColumn: '1 / 4' }} pl={1}>
            {props.durationDisplay}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Transaction type toggle button group with text labels.
 */
function TransactionToggle(toggleProps: {
  hasPhysicalItem: boolean;
  disabled: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  const isDisabled = toggleProps.disabled || !toggleProps.hasPhysicalItem;
  const canInteract = !isDisabled;

  const disabledTooltip = !toggleProps.hasPhysicalItem
    ? 'Sale transactions are not available for service items because there is no ownership transfer'
    : 'Transaction type cannot be changed on subsequent phases';

  const toggle = (
    <ToggleButtonGroup
      exclusive
      size="small"
      disabled={isDisabled}
      value={toggleProps.value}
      onChange={(_event, value) => {
        if (value !== null && canInteract) {
          toggleProps.onChange(value);
        }
      }}
    >
      {canInteract ? (
        <Tooltip
          title="Ownership is retained by your organization and provided as-a-service (HaaS)"
          arrow
        >
          <ToggleButton
            value={TRANSACTION_TYPE.SERVICE}
            sx={{ textTransform: 'none' }}
          >
            <HomeRepairServiceOutlined
              fontSize="small"
              color="info"
              sx={{ mr: 0.5 }}
            />
            Service
          </ToggleButton>
        </Tooltip>
      ) : (
        <ToggleButton
          value={TRANSACTION_TYPE.SERVICE}
          disabled
          sx={{
            textTransform: 'none',
            '&.Mui-disabled.Mui-selected': { color: 'text.secondary' },
          }}
        >
          <HomeRepairServiceOutlined
            fontSize="small"
            color="disabled"
            sx={{ mr: 0.5 }}
          />
          Service
        </ToggleButton>
      )}
      {canInteract ? (
        <Tooltip
          title="Ownership goes to the customer (title transfer) as part of this transaction"
          arrow
        >
          <ToggleButton
            value={TRANSACTION_TYPE.SALE}
            sx={{ textTransform: 'none' }}
          >
            <SellOutlined fontSize="small" color="success" sx={{ mr: 0.5 }} />
            Sale
          </ToggleButton>
        </Tooltip>
      ) : (
        <ToggleButton
          value={TRANSACTION_TYPE.SALE}
          disabled
          sx={{
            textTransform: 'none',
            '&.Mui-disabled.Mui-selected': { color: 'text.secondary' },
          }}
        >
          <SellOutlined fontSize="small" color="disabled" sx={{ mr: 0.5 }} />
          Sale
        </ToggleButton>
      )}
    </ToggleButtonGroup>
  );

  if (isDisabled) {
    return (
      <Tooltip title={disabledTooltip} arrow>
        <Box>{toggle}</Box>
      </Tooltip>
    );
  }

  return toggle;
}
